import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NavBar from "@/components/NavBar";
import SwapPicker from "@/components/SwapPicker";
import ShareCard from "@/components/ShareCard";
import api from "@/lib/api";

const VERDICT_CLASS = { green: "verdict-green", yellow: "verdict-yellow", red: "verdict-red" };
const VERDICT_TITLE = {
  green: "Compatible — go ahead.",
  yellow: "Save it for later.",
  red: "Skip this one.",
};

// Wraps any case-insensitive occurrence of each flagged term in a <mark>.
function highlightIngredients(ingredients, flagged, verdict) {
  if (!ingredients) return null;
  if (!flagged || flagged.length === 0 || verdict === "green") return ingredients;
  const escaped = flagged.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  // Sort longer first so multi-word terms beat single-word substrings
  escaped.sort((a, b) => b.length - a.length);
  const splitRe = new RegExp(`(${escaped.join("|")})`, "gi");
  const testRe = new RegExp(`^(?:${escaped.join("|")})$`, "i");
  const cls = verdict === "red"
    ? "bg-[#f0d7d0] text-[#6e3a30] px-1 rounded font-medium"
    : "bg-[#f3e9d2] text-[#7a5b22] px-1 rounded font-medium";
  const parts = String(ingredients).split(splitRe);
  return parts.map((p, i) => testRe.test(p) ? <mark key={i} className={cls}>{p}</mark> : <span key={i}>{p}</span>);
}

export default function ProductResult() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [showSwap, setShowSwap] = useState(false);
  const [swappedSlot, setSwappedSlot] = useState(null);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setData(r.data)).catch(() => setErr("Couldn't load product."));
  }, [id]);

  const remove = async () => {
    await api.delete(`/products/${id}`);
    nav("/library");
  };

  if (err) return <><NavBar /><div className="max-w-3xl mx-auto px-6 py-16 text-[var(--aa-terra)]">{err}</div></>;
  if (!data) return <><NavBar /><div className="max-w-3xl mx-auto px-6 py-16 aa-eyebrow">Loading verdict…</div></>;

  return (
    <div className="App" data-testid="page-product-result">
      <NavBar />
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="aa-eyebrow mb-3">{data.category.replace("_", " ")} · {data.phase.replace("_", " ")}</div>
            <h1 className="font-display text-3xl lg:text-5xl leading-tight">{data.product_name}</h1>
          </div>
          <span data-testid="result-verdict" className={`aa-pill px-5 py-2 text-base ${VERDICT_CLASS[data.verdict]}`}>
            {data.verdict.toUpperCase()}
          </span>
        </div>

        <div className="aa-card p-7 mt-8 fade-in">
          <div className="font-display text-2xl leading-snug">{VERDICT_TITLE[data.verdict]}</div>
          <p className="mt-3 text-[var(--aa-text-2)]">{data.summary}</p>
          {(data.verdict === "green" || data.verdict === "yellow") && (
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                data-testid="result-swap-btn"
                onClick={() => setShowSwap(true)}
                className="aa-pill px-5 py-3 bg-[var(--aa-text)] text-[var(--aa-bg)] border-[var(--aa-text)] hover:opacity-90"
              >
                {data.verdict === "green" ? "Swap into my protocol →" : "Swap in anyway →"}
              </button>
              {data.verdict === "green" && (
                <button
                  data-testid="result-share-btn"
                  onClick={() => setShowShare(true)}
                  className="aa-pill px-5 py-3 hover:bg-[var(--aa-elevated)]"
                >
                  Share this verdict ↓
                </button>
              )}
              {swappedSlot && (
                <span data-testid="result-swap-confirm" className="aa-pill verdict-green px-4 py-2 text-sm">
                  ✓ Swapped — view it on your Dashboard or My Protocol.
                </span>
              )}
            </div>
          )}
          {data.verdict === "yellow" && (
            <p className="mt-4 text-xs text-[var(--aa-text-3)] italic leading-relaxed max-w-2xl">
              Use "Swap in anyway" only if you have prior experience with this product — for example, you were already on a stronger retinoid before this program. Otherwise, the YELLOW verdict above is the safer path.
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <div className="aa-card p-7 fade-in delay-1" data-testid="result-positives">
            <div className="aa-eyebrow mb-3">In its favor</div>
            {data.positives?.length ? (
              <ul className="space-y-2 list-disc pl-5 text-[var(--aa-text-2)]">
                {data.positives.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            ) : <div className="text-sm text-[var(--aa-text-3)]">No notable bonus features detected.</div>}
          </div>
          <div className="aa-card p-7 fade-in delay-2" data-testid="result-reasons">
            <div className="aa-eyebrow mb-3">What we noticed</div>
            {data.reasons?.length ? (
              <ul className="space-y-2 list-disc pl-5 text-[var(--aa-text-2)]">
                {data.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            ) : <div className="text-sm text-[var(--aa-text-3)]">No concerns flagged for this phase.</div>}
          </div>
        </div>

        {data.substitution_hint && (
          <div className="aa-card p-6 mt-6" data-testid="result-hint">
            <div className="aa-eyebrow mb-2">Substitution hint</div>
            <p className="text-[var(--aa-text-2)]">{data.substitution_hint}</p>
          </div>
        )}

        <div className="aa-card p-6 mt-6">
          <div className="aa-eyebrow mb-2">Ingredient list (as analyzed)</div>
          <p className="text-sm leading-relaxed text-[var(--aa-text-2)]" data-testid="result-ingredients">
            {highlightIngredients(data.ingredients, data.flagged_terms, data.verdict)}
          </p>
          {data.flagged_terms && data.flagged_terms.length > 0 && data.verdict !== "green" && (
            <div className="text-xs text-[var(--aa-text-3)] mt-2 italic" data-testid="result-flagged-legend">
              Highlighted ingredients are what triggered the {data.verdict.toUpperCase()} verdict.
            </div>
          )}
        </div>

        <div className="flex justify-between items-center mt-10">
          <Link to="/analyze" data-testid="result-analyze-another" className="aa-cta-secondary">Analyze another →</Link>
          <button onClick={remove} data-testid="result-delete" className="aa-pill px-4 py-2 hover:bg-[var(--aa-elevated)] text-[var(--aa-text-3)]">Delete</button>
        </div>
      </section>

      {showSwap && (
        <SwapPicker
          product={data}
          onClose={() => setShowSwap(false)}
          onSwapped={(slotKey) => { setSwappedSlot(slotKey); setShowSwap(false); }}
        />
      )}

      {showShare && (
        <ShareCard
          product={data}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  );
}
